/**
 * Dialog360 management types — mirror the backend CCMSLib.Integrations.Dialog360
 * models and the CCMS.Api.Communications api/dialog360/* request DTOs (camelCased).
 * Kept local (not from the SDK) so the admin console does not depend on an SDK rebuild.
 */

// ---- Diagnostics -----------------------------------------------------------
export interface Dialog360ConnectionResult {
  success: boolean;
  statusCode?: number | null;
  phoneNumbersReturned?: number | null;
  /** Channel send capability from /health_status (AVAILABLE / LIMITED / BLOCKED). */
  canSendMessage?: string | null;
  error?: string | null;
}

// ---- Profile / phone numbers ----------------------------------------------
export interface BusinessProfile {
  about?: string | null;
  address?: string | null;
  description?: string | null;
  email?: string | null;
  vertical?: string | null;
  websites?: string[] | null;
  profilePictureHandle?: string | null;
  messagingProduct?: string | null;
}

export interface PhoneNumberInfo {
  id?: string | null;
  displayPhoneNumber?: string | null;
  verifiedName?: string | null;
  qualityRating?: string | null;
  nameStatus?: string | null;
  status?: string | null;
  codeVerificationStatus?: string | null;
}

// ---- Outbound messages (queue + sent history) ------------------------------
export interface AppWhatsAppOutboundMessage {
  messageID: number;
  recipient?: string | null;
  messageType?: string | null;
  body?: string | null;
  caption?: string | null;
  mediaUrl?: string | null;
  templateName?: string | null;
  templateLanguage?: string | null;
  whatsAppMessageId?: string | null;
  externalMessageId?: string | null;
  isSent?: boolean | null;
  hasFailed?: boolean | null;
  status?: string | null;
  failureReason?: string | null;
  errorCode?: string | null;
  response?: string | null;
  retryCount?: number | null;
  maxRetryCount?: number | null;
  priority?: number | null;
  sentDateTime?: string | null;
  deliveredDateTime?: string | null;
  readDateTime?: string | null;
  queuedDateTime?: string | null;
  scheduledDateTime?: string | null;
  createdDateTime?: number | null;
  modifiedDateTime?: number | null;
}

/** Mantine color for an outbound message status (Pending → Read / Failed). */
export function outboundStatusColor(status?: string | null): string {
  switch ((status || '').toLowerCase()) {
    case 'delivered':
    case 'read':
      return 'green';
    case 'sent':
      return 'teal';
    case 'processing':
      return 'blue';
    case 'pending':
      return 'gray';
    case 'failed':
      return 'red';
    case 'skipped':
      // Not sent by WhatsApp on purpose (no approved template, outside the 24-hour window, a security message);
      // the reason, and the SMS that went instead, are on the row.
      return 'orange';
    default:
      return 'gray';
  }
}

/** Format an ISO-8601 datetime string (DateTime fields) to a readable local string. */
export function formatIsoDateTime(value?: string | null): string {
  if (!value) return '-';
  const d = new Date(value);
  return isNaN(d.getTime()) ? value : d.toLocaleString();
}

// ---- Inbound messages ------------------------------------------------------
export interface AppWhatsAppInboundMessage {
  inboundMessageID: number;
  waMessageId?: string | null;
  fromNumber?: string | null;
  contactName?: string | null;
  messageType?: string | null;
  body?: string | null;
  mediaId?: string | null;
  mimeType?: string | null;
  caption?: string | null;
  interactiveType?: string | null;
  replyId?: string | null;
  replyTitle?: string | null;
  reactionEmoji?: string | null;
  contextMessageId?: string | null;
  rawPayload?: string | null;
  status?: string | null;
  receivedDateTime?: number | null;
  processedDateTime?: number | null;
  createdDateTime?: number | null;
  modifiedDateTime?: number | null;
}

// ---- Cloud (360dialog) templates ------------------------------------------
export const TEMPLATE_CATEGORIES = ['UTILITY', 'MARKETING', 'AUTHENTICATION'] as const;
export type TemplateCategory = (typeof TEMPLATE_CATEGORIES)[number];

export interface TemplateButtonDefinition {
  type: 'QUICK_REPLY' | 'URL' | 'PHONE_NUMBER';
  text: string;
  url?: string;
  phoneNumber?: string;
}

export interface TemplateDefinitionExample {
  headerText?: string[];
  bodyText?: string[][];
  headerHandle?: string[];
}

export interface TemplateDefinitionComponent {
  type: 'HEADER' | 'BODY' | 'FOOTER' | 'BUTTONS';
  format?: string | null;
  text?: string | null;
  example?: TemplateDefinitionExample | null;
  buttons?: TemplateButtonDefinition[] | null;
}

export interface CreateTemplateApiRequest {
  name: string;
  category: TemplateCategory | string;
  language: string;
  components: TemplateDefinitionComponent[];
}

export interface EditTemplateApiRequest {
  category?: string | null;
  components: TemplateDefinitionComponent[];
}

export interface TemplateListItem {
  id?: string | null;
  externalId?: string | null;
  name: string;
  language?: string | null;
  status?: string | null;
  category?: string | null;
  namespace?: string | null;
  rejectedReason?: string | null;
  createdAt?: string | null;
  modifiedAt?: string | null;
  components?: TemplateDefinitionComponent[] | null;
}

export interface TemplateMutationResponse {
  id?: string | null;
  status?: string | null;
  category?: string | null;
  error?: { code: number; message: string } | null;
}

// ---- Effective config (api/dialog360/config) -------------------------------
/** Read-only view of the live 360dialog config (DB override merged over appsettings). */
export interface Dialog360Config {
  apiUrl?: string | null;
  phoneNumberId?: string | null;
  wabaId?: string | null;
  billingCurrency?: string | null;
  apiKeySet: boolean;
  apiKeyMasked?: string | null;
  webhookSecretSet: boolean;
  webhookVerifyTokenSet: boolean;
}

// ---- Billing / usage (api/dialog360/billing) -------------------------------
export interface BillingCategoryBreakdown {
  category: string;
  conversations: number;
  cost: number;
}

export interface BillingTrendPoint {
  date: string; // yyyy-MM-dd
  conversations: number;
  cost: number;
}

/** Real WhatsApp spend (Meta conversation analytics via 360dialog), aggregated by the backend. */
export interface Dialog360Billing {
  start: string; // yyyy-MM-dd
  end: string;
  granularity: string;
  currency?: string | null;
  totalCost: number;
  totalConversations: number;
  categories: BillingCategoryBreakdown[];
  trend: BillingTrendPoint[];
}

/** Gradient variant (for library Progress/SegmentedControl) keyed by category. */
export type CategoryGradientVariant = 'primary' | 'secondary' | 'tertiary' | 'success' | 'info' | 'warning' | 'danger';

/** Human label + Mantine color + gradient variant for a WhatsApp conversation category. */
export function conversationCategoryMeta(category: string): { label: string; color: string; gradientVariant: CategoryGradientVariant } {
  switch ((category || '').toUpperCase()) {
    case 'MARKETING':
      return { label: 'Marketing', color: 'grape', gradientVariant: 'tertiary' };
    case 'UTILITY':
      return { label: 'Utility', color: 'blue', gradientVariant: 'info' };
    case 'AUTHENTICATION':
    case 'AUTHENTICATION_INTERNATIONAL':
      return { label: 'Authentication', color: 'teal', gradientVariant: 'success' };
    case 'SERVICE':
      return { label: 'Service', color: 'green', gradientVariant: 'primary' };
    case 'MARKETING_LITE':
      return { label: 'Marketing Lite', color: 'pink', gradientVariant: 'secondary' };
    default:
      return { label: category || 'Unknown', color: 'gray', gradientVariant: 'primary' };
  }
}

// ---- Send request bodies (api/dialog360/send/*) ----------------------------
export interface SendTextReq {
  recipient: string;
  body: string;
  previewUrl?: boolean;
  contextMessageId?: string;
}

export interface SendMediaReq {
  recipient: string;
  mediaId?: string;
  link?: string;
  caption?: string;
  filename?: string;
  contextMessageId?: string;
}

export interface SendTemplateReq {
  recipient: string;
  templateName: string;
  languageCode: string;
  contextMessageId?: string;
}

export interface SendLocationReq {
  recipient: string;
  latitude: number;
  longitude: number;
  name?: string;
  address?: string;
  contextMessageId?: string;
}

export interface SendContactsReq {
  recipient: string;
  /** vCard-style contact objects (see backend ContactObject); parsed from JSON in the UI. */
  contacts: unknown[];
  contextMessageId?: string;
}

export interface InteractiveButtonReply {
  id: string;
  title: string;
}

export interface SendInteractiveButtonsReq {
  recipient: string;
  bodyText: string;
  buttons: InteractiveButtonReply[];
  headerText?: string;
  footerText?: string;
  contextMessageId?: string;
}

export interface InteractiveRow {
  id: string;
  title: string;
  description?: string;
}

export interface InteractiveSection {
  title?: string;
  rows: InteractiveRow[];
}

export interface SendInteractiveListReq {
  recipient: string;
  bodyText: string;
  buttonText: string;
  sections: InteractiveSection[];
  headerText?: string;
  footerText?: string;
  contextMessageId?: string;
}

export interface SendCtaUrlReq {
  recipient: string;
  bodyText: string;
  displayText: string;
  url: string;
  headerText?: string;
  footerText?: string;
  contextMessageId?: string;
}

export interface SendReactionReq {
  recipient: string;
  messageId: string;
  emoji: string;
}

/** The set of message types the Compose tab can send. */
export type ComposeMessageType =
  | 'text'
  | 'image'
  | 'video'
  | 'audio'
  | 'document'
  | 'sticker'
  | 'template'
  | 'location'
  | 'contacts'
  | 'buttons'
  | 'list'
  | 'cta'
  | 'reaction';

// ---- Display helpers -------------------------------------------------------

/** Mantine color for a 360dialog template approval status. */
export function templateStatusColor(status?: string | null): string {
  switch ((status || '').toUpperCase()) {
    case 'APPROVED':
      return 'green';
    case 'PENDING':
      return 'yellow';
    case 'REJECTED':
      return 'red';
    case 'PAUSED':
    case 'DISABLED':
      return 'orange';
    default:
      return 'gray';
  }
}

/** Mantine color for a phone-number quality rating. */
export function qualityRatingColor(rating?: string | null): string {
  switch ((rating || '').toUpperCase()) {
    case 'GREEN':
      return 'green';
    case 'YELLOW':
      return 'yellow';
    case 'RED':
      return 'red';
    default:
      return 'gray';
  }
}

/** Format a yyyyMMddHHmmss decimal timestamp (as used by the backend) to a readable string. */
export function formatStampDecimal(stamp?: number | null): string {
  if (!stamp) return '-';
  const s = String(Math.trunc(stamp));
  if (s.length !== 14) return s;
  const y = s.slice(0, 4);
  const mo = s.slice(4, 6);
  const d = s.slice(6, 8);
  const h = s.slice(8, 10);
  const mi = s.slice(10, 12);
  const se = s.slice(12, 14);
  return `${y}-${mo}-${d} ${h}:${mi}:${se}`;
}
